import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { Grid } from '@zillow/constellation';
import { LoadingParagraph, LoadingTag } from '@zg-rentals/loading-components';

import { useSelector } from '../../selectors/useSelector';

import { useGetPaymentsSummary } from '../../hooks/queries/summary';

import { PAYMENT_STATUS_TEXT_TO_TYPE } from '../../constants/PaymentConstants';

import SelectPropertyConstants from '../../constants/SelectPropertyConstants';

import numberUtils from '../../utils/numberUtils';
import selectPropertyUtils from '../../utils/selectPropertyUtils';

import { getTagTypeByKey, STATUS_KEY } from '../../utils/statusTagUtils';

import ErrorMessagesAlert from '../ErrorMessagesAlert';

import SimpleCard from '../SimpleCard';
import ConnectedModuleCard from '../../components/ConnectedModuleCard';

import { NoUnderlineAnchor } from '../../styles';

const { PAYMENT_SUMMARY } = STATUS_KEY;

const PaymentsModule = () => {
  const { listingAlias } = useSelector((state) => {
    return {
      listingAlias: state.properties.currentProperty.listingAlias,
    };
  }, shallowEqual);

  const {
    data = {
      paymentCardList: [{ title: '', amount: 0, paymentTracker: { statusText: '' } }],
    },
    isPending,
    error,
  } = useGetPaymentsSummary(listingAlias);

  // use listingAlias from currentProperty as this is not returned in the /summary/payments endpoint
  const paymentsPath = selectPropertyUtils.getPathByType(
    SelectPropertyConstants.PAYMENTS as keyof typeof SelectPropertyConstants,
    listingAlias,
  );

  const paymentCardListLength = data?.paymentCardList?.length || 0;
  const gridRepeat = paymentCardListLength < 2 ? 1 : 2;

  const renderCTA = useMemo(() => {
    return (
      <NoUnderlineAnchor href={paymentsPath || ''} target="_self">
        Details
      </NoUnderlineAnchor>
    );
  }, [paymentsPath]);

  const renderNoPayments = useMemo(() => {
    return (
      <Grid>
        <SimpleCard body={'Set up payments'} bodyHeading={'Upcoming payments will appear here'} withChevron={false} />
      </Grid>
    );
  }, [isPending]);

  const renderPaymentCardList = (paymentCardItem: {
    title: string;
    amount: number;
    paymentTracker: { statusText: string };
  }) => {
    const {
      title,
      amount,
      paymentTracker: { statusText },
    } = paymentCardItem;
    const displayAmount = numberUtils.asCurrency(amount / 100);

    const toneTypeKey = PAYMENT_STATUS_TEXT_TO_TYPE[statusText.toLowerCase()] || 'neutral';
    const tone = getTagTypeByKey(PAYMENT_SUMMARY, toneTypeKey);

    return (
      <Grid key={`PaymentCard-${title}`}>
        <SimpleCard withChevron={false}>
          <LoadingTag
            data-testid="PaymentCard-Status"
            size="sm"
            tone={tone}
            css={{ margin: '0 {spacing.200} {spacing.200} 0' }}
            isLoading={isPending}
            loadingWidth="10%"
          >
            {statusText}
          </LoadingTag>
          <LoadingParagraph
            data-testid="PaymentCard-Title"
            textStyle="body-sm-bold"
            isLoading={isPending}
            loadingWidth="40%"
            css={{ fontSize: { lg: 'body' }, marginBottom: '200' }}
          >
            {title}
          </LoadingParagraph>
          <LoadingParagraph
            data-testid="PaymentCard-Amount"
            textStyle="body-sm"
            isLoading={isPending}
            loadingWidth="20%"
          >
            {displayAmount}
          </LoadingParagraph>
        </SimpleCard>
      </Grid>
    );
  };

  return (
    <ConnectedModuleCard
      data-testid="PaymentsModule"
      title="Payments"
      ctaComponent={!error && renderCTA}
      ctaFontColor="text.subtle"
      ctaFontType="body-sm"
      justifyHeadingContent={'center'}
    >
      {error ? (
        <ErrorMessagesAlert
          appearance="warning"
          data-testid="PaymentsModuleError"
          errorMessages={error.error.data.partialMessages || [{ text: error.error.message }]}
        />
      ) : (
        <Grid
          display="grid"
          gridTemplateColumns={{ default: 'repeat(1, 1fr)', lg_gte: `repeat(${gridRepeat}, minmax(0,1fr))` }}
          gap={{ default: 'xs', lg: 'sm' }}
        >
          {paymentCardListLength > 0 ? data?.paymentCardList.map(renderPaymentCardList) : renderNoPayments}
        </Grid>
      )}
    </ConnectedModuleCard>
  );
};

export default PaymentsModule;
